"use client";

import { type FormEvent, useEffect, useState } from "react";
import Image from "next/image";
import { X } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import searchIcon from "public/icons/search.svg";
import { cn } from "@/lib/utils";

interface SearchQueryFieldProps {
  initialQuery?: string;
}

const searchInputClassName = cn(
  "min-w-0 flex-1 bg-transparent font-sans text-base text-tag-foreground focus:outline-none",
  "[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none"
);

const searchIconButtonClassName =
  "flex size-6 shrink-0 items-center justify-center text-foreground transition-colors hover:text-primary";

/** Поле поиска в шапке страницы результатов — Figma hero search. */
export function SearchQueryField({ initialQuery = "" }: SearchQueryFieldProps) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [query, setQuery] = useState(initialQuery);

  useEffect(() => {
    const queryParam = searchParams.get("q");
    if (queryParam) {
      queueMicrotask(() => setQuery(queryParam));
    }
  }, [searchParams]);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (query.trim().length < 2) return;
    router.push(`/search?q=${encodeURIComponent(query.trim())}`);
  };

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-[880px]">
      <div className="flex flex-col gap-4">
        <div className="flex items-end justify-between gap-4">
          <label className="sr-only" htmlFor="search-query">
            Поисковый запрос
          </label>
          <input
            id="search-query"
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            className={searchInputClassName}
          />
          {query.length > 0 && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className={searchIconButtonClassName}
              aria-label="Очистить поиск"
            >
              <X className="size-6" strokeWidth={1.5} aria-hidden />
            </button>
          )}
          <button
            type="submit"
            className={searchIconButtonClassName}
            aria-label="Найти"
          >
            <Image src={searchIcon} alt="" width={24} height={24} className="size-6" />
          </button>
        </div>
        <span className="block h-px w-full bg-border" aria-hidden />
      </div>
    </form>
  );
}
